import React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  LinearProgress,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { Link as RouterLink } from "react-router-dom";

import { useDonationsStore, Donation } from "../../store/useDonationsStore";
import { useErrorStore } from "../../store/useErrorStore";
import { downloadDonationReceipt, searchDonations as searchDonationsApi } from "../../api";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getFinancialYearLabel = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = date.getMonth();
  const startYear = month >= 3 ? year : year - 1;
  const endYear = startYear + 1;
  return `${startYear}-${String(endYear).slice(-2)}`;
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const valueA = a[orderBy];
  const valueB = b[orderBy];

  if (valueA === undefined || valueA === null) {
    return 1;
  }
  if (valueB === undefined || valueB === null) {
    return -1;
  }

  if (typeof valueA === "number" && typeof valueB === "number") {
    return valueB - valueA;
  }

  const stringA = String(valueA).toLowerCase();
  const stringB = String(valueB).toLowerCase();
  if (stringB < stringA) {
    return -1;
  }
  if (stringB > stringA) {
    return 1;
  }
  return 0;
}

function getComparator(order: "asc" | "desc", orderBy: keyof Donation) {
  return order === "desc"
    ? (a: Donation, b: Donation) => descendingComparator(a, b, orderBy)
    : (a: Donation, b: Donation) => -descendingComparator(a, b, orderBy);
}

const DONATION_ROW_OPTIONS = [25, 50, 100];

export default function DonationTable() {
  const { donations: storeDonations, loading } = useDonationsStore();
  const { error, setError } = useErrorStore();

  const [donations, setDonations] = React.useState<Donation[]>([]);
  const [originalDonations, setOriginalDonations] = React.useState<Donation[]>([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [isSearching, setIsSearching] = React.useState(false);
  const [filterYear, setFilterYear] = React.useState<string>("all");
  const [filterPurpose, setFilterPurpose] = React.useState<string>("all");
  const [filterHead, setFilterHead] = React.useState<string>("all");
  const [filterEvent, setFilterEvent] = React.useState<string>("all");
  const [order, setOrder] = React.useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = React.useState<keyof Donation>("date");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  React.useEffect(() => {
    setDonations(storeDonations);
    setOriginalDonations(storeDonations);
  }, [storeDonations]);

  React.useEffect(() => {
    if (!searchTerm) {
      setDonations(originalDonations);
      setIsSearching(false);
      setError(null);
      return;
    }

    setIsSearching(true);
    const handle = window.setTimeout(async () => {
      try {
        const result = await searchDonationsApi(searchTerm);
        setDonations(Array.isArray(result) ? (result as Donation[]) : []);
        setError(null);
      } catch (err) {
        console.error("Donation search failed", err);
        setError("Unable to search donations right now.");
      } finally {
        setIsSearching(false);
      }
    }, 320);

    return () => window.clearTimeout(handle);
  }, [searchTerm, originalDonations, setError]);

  const filterOptions = React.useMemo(() => {
    const yearSet = new Set<string>();
    const purposeSet = new Set<string>();
    const headSet = new Set<string>();
    const eventSet = new Set<string>();

    originalDonations.forEach((donation) => {
      const yearLabel = getFinancialYearLabel(donation.date);
      if (yearLabel) {
        yearSet.add(yearLabel);
      }
      if (donation.purpose) {
        purposeSet.add(donation.purpose);
      }
      if (donation.head) {
        headSet.add(donation.head);
      }
      if (donation.event) {
        eventSet.add(donation.event);
      }
    });

    const sortLabels = (values: Set<string>) => Array.from(values).sort((a, b) => a.localeCompare(b));

    return {
      years: Array.from(yearSet).sort((a, b) => b.localeCompare(a)),
      purposes: sortLabels(purposeSet),
      heads: sortLabels(headSet),
      events: sortLabels(eventSet),
    };
  }, [originalDonations]);

  const filteredDonations = React.useMemo(() => {
    return donations.filter((donation) => {
      const yearLabel = getFinancialYearLabel(donation.date);

      if (filterYear !== "all" && yearLabel !== filterYear) {
        return false;
      }
      if (filterPurpose !== "all" && donation.purpose !== filterPurpose) {
        return false;
      }
      if (filterHead !== "all" && donation.head !== filterHead) {
        return false;
      }
      if (filterEvent !== "all" && donation.event !== filterEvent) {
        return false;
      }

      return true;
    });
  }, [donations, filterYear, filterPurpose, filterHead, filterEvent]);

  const sortedDonations = React.useMemo(
    () => [...filteredDonations].sort(getComparator(order, orderBy)),
    [filteredDonations, order, orderBy],
  );

  const paginatedDonations = React.useMemo(
    () => sortedDonations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedDonations, page, rowsPerPage],
  );

  const pageCount = Math.max(1, Math.ceil(filteredDonations.length / rowsPerPage));

  const handleRequestSort = (property: keyof Donation) => {
    if (orderBy === property) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrder("desc");
      setOrderBy(property);
    }
  };

  const handleDownloadReceipt = async (donationId: number) => {
    try {
      const blob = await downloadDonationReceipt(donationId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `donation-receipt-${donationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download receipt", err);
      setError("Unable to download the donation receipt.");
    }
  };

  const resetFilters = () => {
    setFilterYear("all");
    setFilterPurpose("all");
    setFilterHead("all");
    setFilterEvent("all");
    setPage(0);
  };

  const renderSortIcon = (property: keyof Donation) => (
    <ArrowDownwardRoundedIcon
      sx={{
        fontSize: 16,
        transition: "0.2s",
        opacity: orderBy === property ? 1 : 0.4,
        transform: orderBy === property && order === "asc" ? "rotate(180deg)" : "rotate(0deg)",
      }}
    />
  );

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: { xs: 2.5, md: 3 },
        display: { xs: "none", sm: "flex" },
        flexDirection: "column",
        gap: 2.5,
        boxShadow: "sm",
        overflow: "hidden",
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <Stack spacing={0.5}>
        <Typography level="title-lg" sx={{ fontWeight: 700 }}>
          Donation ledger
        </Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 560 }}>
          Review and steward every offering made to the temple. Apply filters to trace donations by campaign, seva head, or
          financial year.
        </Typography>
      </Stack>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems={{ lg: "flex-end" }}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Search devotees, payment reference, or purpose</FormLabel>
          <Input
            size="sm"
            placeholder="Search donations"
            value={searchTerm}
            startDecorator={<SearchRoundedIcon />}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setPage(0);
            }}
          />
        </FormControl>

        <FormControl sx={{ minWidth: { xs: "100%", sm: 180 } }}>
          <FormLabel>Financial year</FormLabel>
          <Select
            value={filterYear}
            size="sm"
            onChange={(_, value) => {
              setFilterYear(value ?? "all");
              setPage(0);
            }}
          >
            <Option value="all">All years</Option>
            {filterOptions.years.map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <FormLabel>Purpose</FormLabel>
          <Select
            value={filterPurpose}
            size="sm"
            onChange={(_, value) => {
              setFilterPurpose(value ?? "all");
              setPage(0);
            }}
          >
            <Option value="all">All purposes</Option>
            {filterOptions.purposes.map((purpose) => (
              <Option key={purpose} value={purpose}>
                {purpose}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <FormLabel>Seva head</FormLabel>
          <Select
            value={filterHead}
            size="sm"
            onChange={(_, value) => {
              setFilterHead(value ?? "all");
              setPage(0);
            }}
          >
            <Option value="all">All heads</Option>
            {filterOptions.heads.map((head) => (
              <Option key={head} value={head}>
                {head}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <FormLabel>Event</FormLabel>
          <Select
            value={filterEvent}
            size="sm"
            onChange={(_, value) => {
              setFilterEvent(value ?? "all");
              setPage(0);
            }}
          >
            <Option value="all">All events</Option>
            {filterOptions.events.map((event) => (
              <Option key={event} value={event}>
                {event}
              </Option>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="plain"
          color="neutral"
          startDecorator={<FilterAltOffRoundedIcon />}
          onClick={resetFilters}
        >
          Clear filters
        </Button>
      </Stack>

      <Sheet
        variant="outlined"
        sx={{
          borderRadius: "lg",
          overflow: "hidden",
          position: "relative",
          maxWidth: "960px",
          overflowX: "auto",
          "--TableCell-headBackground": "var(--joy-palette-background-level1)",
        }}
      >
        {(loading || isSearching) && (
          <LinearProgress sx={{ position: "absolute", insetInline: 0, top: 0, zIndex: 1 }} />
        )}
        <Table
          stickyHeader
          borderAxis="bothBetween"
          hoverRow
          size="sm"
          sx={{
            width: "100%",
            "--TableCell-headBackground": "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "12px",
            "--TableCell-paddingX": "16px",
            "& tbody tr": {
              "& td": {
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }
            }
          }}
        >
          <thead>
            <tr>
              <th scope="col" style={{ width: 70 }}>
                <Button
                  size="sm"
                  variant="plain"
                  color={orderBy === "id" ? "primary" : "neutral"}
                  endDecorator={renderSortIcon("id")}
                  onClick={() => handleRequestSort("id")}
                  sx={{ px: 0 }}
                >
                  ID
                </Button>
              </th>
              <th scope="col" style={{ width: 110 }}>
                <Button
                  size="sm"
                  variant="plain"
                  color={orderBy === "date" ? "primary" : "neutral"}
                  endDecorator={renderSortIcon("date")}
                  onClick={() => handleRequestSort("date")}
                  sx={{ px: 0 }}
                >
                  Date
                </Button>
              </th>
              <th scope="col" style={{ width: 220 }}>Devotee</th>
              <th scope="col" style={{ width: 110 }}>
                <Button
                  size="sm"
                  variant="plain"
                  color={orderBy === "amount" ? "primary" : "neutral"}
                  endDecorator={renderSortIcon("amount")}
                  onClick={() => handleRequestSort("amount")}
                  sx={{ px: 0 }}
                >
                  Amount
                </Button>
              </th>
              <th scope="col" style={{ width: 180 }}>Purpose &amp; seva head</th>
              <th scope="col" style={{ width: 120 }}>Payment</th>
              <th scope="col" style={{ width: 150 }}>Reference</th>
              <th scope="col" style={{ width: 130 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td colSpan={8}>
                  <Typography level="body-sm" sx={{ py: 3, textAlign: "center" }} color="danger">
                    {error}
                  </Typography>
                </td>
              </tr>
            ) : paginatedDonations.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <Stack spacing={1} sx={{ py: 4, alignItems: "center" }}>
                    <ReceiptLongRoundedIcon color="primary" />
                    <Typography level="title-sm">No donations match the current filters.</Typography>
                    <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 420, textAlign: "center" }}>
                      Try widening your filters or clearing the search to rediscover every devotee’s generous offerings.
                    </Typography>
                  </Stack>
                </td>
              </tr>
            ) : (
              paginatedDonations.map((donation) => (
                <tr key={donation.id}>
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {donation.id}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">{formatDate(donation.date)}</Typography>
                  </td>
                  <td>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar size="sm">
                        {donation.name
                          ? donation.name
                              .split(" ")
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((part) => part[0]!.toUpperCase())
                              .join("")
                          : "?"}
                      </Avatar>
                      <Box>
                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                          {donation.name || "Anonymous devotee"}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                          {donation.email || donation.phone || "–"}
                        </Typography>
                      </Box>
                    </Stack>
                  </td>
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {currencyFormatter.format(Number(donation.amount ?? 0))}
                    </Typography>
                  </td>
                  <td>
                    <Stack spacing={0.5}>
                      <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        {donation.purpose || "–"}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {donation.head || donation.subPurpose || donation.event || "–"}
                      </Typography>
                    </Stack>
                  </td>
                  <td>
                    <Stack spacing={0.5}>
                      <Chip size="sm" variant="soft" color="neutral" sx={{ alignSelf: "flex-start" }}>
                        {donation.paymentMethod || "–"}
                      </Chip>
                      <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {donation.campaignId ? `Campaign: ${donation.campaignId}` : ""}
                      </Typography>
                    </Stack>
                  </td>
                  <td>
                    <Stack spacing={0.5}>
                      <Typography level="body-sm" sx={{ fontWeight: 600, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {donation.txnRefId || "–"}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {donation.taxReceiptNo ? `Receipt ${donation.taxReceiptNo}` : ""}
                      </Typography>
                    </Stack>
                  </td>
                  <td>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="sm"
                        variant="soft"
                        startDecorator={<VisibilityRoundedIcon fontSize="small" />}
                        component={RouterLink}
                        to={`/donations/details?id=${donation.id}`}
                      >
                        View
                      </Button>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="primary"
                        onClick={() => handleDownloadReceipt(donation.id)}
                      >
                        <DownloadRoundedIcon />
                      </IconButton>
                    </Stack>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center" justifyContent="space-between">
        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
          Showing {paginatedDonations.length === 0 ? 0 : page * rowsPerPage + 1}–
          {Math.min(filteredDonations.length, (page + 1) * rowsPerPage)} of {filteredDonations.length} offerings
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl orientation="horizontal" size="sm">
            <FormLabel>Rows per page</FormLabel>
            <Select
              value={rowsPerPage}
              onChange={(_, value) => {
                if (!value) return;
                setRowsPerPage(value);
                setPage(0);
              }}
            >
              {DONATION_ROW_OPTIONS.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={page === 0}
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
            >
              <KeyboardArrowLeftRoundedIcon />
            </IconButton>
            <IconButton
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={page >= pageCount - 1}
              onClick={() => setPage((prev) => Math.min(prev + 1, pageCount - 1))}
            >
              <KeyboardArrowRightRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}
